import { FC, FormEvent, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { AxiosError } from "@follosoft/react-components";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import useRefuseQuoteSchema, { RefuseQuoteFormValues } from "../hooks/use-refuse-quote-schema";
import { useRefuseQuoteService } from "../services/use-refuse-quote-service";
import { REFUSAL_REASON_MAX_LENGTH } from "../constants";

interface Props {
  contractId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRefused: () => void;
}

const RefuseQuoteDialog: FC<Props> = ({ contractId, open, onOpenChange, onRefused }) => {
  const { t } = useTranslation(["quote-refusal"]);
  const schema = useRefuseQuoteSchema();
  const { mutate, isPending, error, reset } = useRefuseQuoteService(contractId);

  const form = useForm<RefuseQuoteFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: "" },
  });

  useEffect(() => {
    if (open) {
      form.reset({ reason: "" });
      reset();
    }
  }, [open, form, reset]);

  const onSubmit = ({ reason }: RefuseQuoteFormValues) =>
    mutate(reason, {
      onSuccess: () => {
        onOpenChange(false);
        onRefused();
      },
    });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.stopPropagation();
    form.handleSubmit(onSubmit)(event);
  };

  const errorMessage = error
    ? (error as AxiosError<{ message?: string }>).response?.data?.message ??
      t("quote-refusal:refuse-failed", "The quote could not be declined. Please try again.")
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>{t("quote-refusal:refuse-title", "Decline this quote?")}</DialogTitle>
          <DialogDescription>
            {t(
              "quote-refusal:refuse-description",
              "Your contractor will see that you declined it. You can come back to this quote later if you change your mind.",
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("quote-refusal:refuse-reason-label", "Reason (optional)")}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={4}
                      maxLength={REFUSAL_REASON_MAX_LENGTH}
                      placeholder={t(
                        "quote-refusal:refuse-reason-placeholder",
                        "Let your contractor know why, if you'd like.",
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {errorMessage && (
              <p className="text-sm text-destructive" role="alert">
                {errorMessage}
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" disabled={isPending} onClick={() => onOpenChange(false)}>
                {t("quote-refusal:refuse-dismiss", "Go back")}
              </Button>
              <Button type="submit" variant="destructive" disabled={isPending}>
                {isPending && <Loader2 size={15} className="animate-spin" />}
                {t("quote-refusal:refuse-confirm", "Decline the quote")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default RefuseQuoteDialog;
